import { useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import sum from 'lodash/sum';
import { getArray } from '../../../utils/typeUtils';
import { DesktopTHead } from './DesktopTHead';
import { TableContentContainer } from '../TableContentContainer';
import { InfoRow } from '../InfoRow';
import { DesktopTableContent } from './DesktopTBody/DesktopTableContent';

export const DesktopScrollableTable = ({
  rows,
  nextData,
  existsNextData,
  isLoading,
  isScrollLoading,
  scrollableTarget,
  headerOptions,
  stickyHeader: stickyHeaderParams,
}) => {
  const headerRef = useRef(null);
  const stickyHeaderBackdropRef = useRef(null);
  const stickyHeaderRef = useRef(null);
  const tableWrapperRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const stickyHeaderThresholdRef = useRef(0);
  const stickyHeaderOnlyRef = useRef(false);
  const scrollContainerId = stickyHeaderParams?.scrollContainerId;
  const stickyHeaderBackdropHeight = stickyHeaderParams?.backdropHeight || 0;
  
  const syncLayout = () => {
    const header = headerRef.current;
    const stickyHeader = stickyHeaderRef.current;
    const stickyHeaderBackdrop = stickyHeaderBackdropRef.current;
    const tableWrapper = tableWrapperRef.current;
    
    if (!header || !stickyHeader || !tableWrapper) {
      return;
    }
  
    const fixedElements = getArray(stickyHeaderParams?.fixedElements);
    const fixedOffsets = getArray(stickyHeaderParams?.fixedOffsets);
    let newThreshold = 0;
    
    if (fixedElements.length) {
      const elements = document.querySelectorAll(fixedElements.join(','));
  
      for (const element of elements) {
        const elementRect = element.getBoundingClientRect();
        newThreshold += elementRect.height;
      }
    }
  
    stickyHeaderThresholdRef.current = sum([newThreshold, ...fixedOffsets]);
  
    const tableWrapperRect = tableWrapper.getBoundingClientRect();
    const headerRect = header.getBoundingClientRect();
    const headerThs = header.querySelectorAll('th');
    const stickyHeaderThs = stickyHeader.querySelectorAll('th');
  
    headerThs.forEach((th, i) => {
      if (stickyHeaderThs[i]) {
        const rect = th.getBoundingClientRect();
        stickyHeaderThs[i].style.width = `${rect.width}px`;
        stickyHeaderThs[i].style.maxWidth = 'unset';
      }
    });
    
    const wrapperWidth = tableWrapperRect.width;
    const left = tableWrapperRect.left;
    const threshold = stickyHeaderThresholdRef.current;
  
    if (stickyHeaderBackdrop) {
      stickyHeaderBackdrop.style.height = `${stickyHeaderBackdropHeight}px`;
      stickyHeaderBackdrop.style.width = `${wrapperWidth}px`;
      stickyHeaderBackdrop.style.left = `${left}px`;
      stickyHeaderBackdrop.style.top = `${threshold - stickyHeaderBackdropHeight}px`;
    }
  
    stickyHeader.style.height = `${headerRect.height + 1}px`;
    stickyHeader.style.width = `${wrapperWidth}px`;
    stickyHeader.style.left = `${left}px`;
    stickyHeader.style.top = `${threshold}px`;
    
    const stickyHeaderTable = stickyHeader.querySelector('table');
    
    if (stickyHeaderTable) {
      stickyHeaderTable.style.width = `${headerRect.width}px`;
    }
  };
  
  useEffect(() => {
    if (scrollContainerId) {
      scrollContainerRef.current = document.getElementById(scrollContainerId);
    }
  }, [scrollContainerId]);
  
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    
    if (!scrollContainer) {
      return;
    }
    
    syncLayout();
    const resizeObserver = new ResizeObserver(syncLayout);
    
    if (scrollContainer) {
      resizeObserver.observe(scrollContainer);
    }
    
    window.addEventListener('resize', syncLayout);
    
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', syncLayout);
    };
  }, [rows]);
  
  useEffect(() => {
    const tableWrapper = tableWrapperRef.current;
    const stickyHeader = stickyHeaderRef.current;
    
    if (!tableWrapper || !stickyHeader) {
      return;
    }
    
    const threshold = stickyHeaderThresholdRef.current;
    const tableRect = tableWrapper.getBoundingClientRect();
    
    if (tableRect.top === threshold) {
      stickyHeaderOnlyRef.current = true;
      stickyHeader.style.opacity = '1';
    }
  }, []);
  
  useEffect(() => {
    if (stickyHeaderOnlyRef.current) {
      return;
    }
    
    const scrollContainer = scrollContainerRef?.current;
    const stickyHeader = stickyHeaderRef.current;
    const stickyHeaderBackdrop = stickyHeaderBackdropRef.current;
    const tableWrapper = tableWrapperRef.current;
    
    if (!scrollContainer || !stickyHeader || !tableWrapper) {
      return;
    }
  
    let lastScrollTop = scrollContainer.scrollTop;
    let ticking = false;
    let isVisible = false;
    
    const onScroll = () => {
      if (ticking) {
        return;
      }
      
      ticking = true;
      
      requestAnimationFrame(() => {
        const threshold = stickyHeaderThresholdRef.current;
        const tableRect = tableWrapper.getBoundingClientRect();
        const scrollTop = scrollContainer.scrollTop;
        const direction = scrollTop > lastScrollTop ? 'down' : 'up';
        const shouldDisplay = direction === 'down' ? tableRect.top <= threshold : tableRect.top <= threshold - 2;
        lastScrollTop = scrollTop;
        
        if (!isVisible) {
          if (shouldDisplay) {
            if (stickyHeaderBackdrop) {
              stickyHeaderBackdrop.style.opacity = '1';
            }
            
            stickyHeader.style.opacity = '1';
            isVisible = true;
          }
        } else {
          if (!shouldDisplay) {
            if (stickyHeaderBackdrop) {
              stickyHeaderBackdrop.style.opacity = '0';
            }
            
            stickyHeader.style.opacity = '0';
            isVisible = false;
          }
        }
        
        ticking = false;
      });
    };
    
    scrollContainer.addEventListener('scroll', onScroll, {
      passive: true,
    });
    
    return () => {
      scrollContainer.removeEventListener('scroll', onScroll);
    };
  }, []);
  
  useEffect(() => {
    if (!scrollContainerRef.current) {
      return;
    }
    
    const stickyHeader = stickyHeaderRef.current;
    const tableWrapper = tableWrapperRef.current;
    
    if (!tableWrapper || !stickyHeader) {
      return;
    }
    
    const stickyHeaderTable = stickyHeader.querySelector('table');
    
    if (!stickyHeaderTable) {
      return;
    }
    
    let ticking = false;
    
    const onScroll = event => {
      if (ticking) {
        return;
      }
      
      requestAnimationFrame(() => {
        stickyHeaderTable.style.transform = `translate3d(${-event.target.scrollLeft}px, 0, 0)`;
        ticking = false;
      });
      
      ticking = true;
    };
  
    tableWrapper.addEventListener('scroll', onScroll, {
      passive: true,
    });
    
    return () => {
      tableWrapper.removeEventListener('scroll', onScroll);
    };
  }, []);
  
  return (
    <InfiniteScroll
      dataLength={rows.length}
      next={nextData}
      hasMore={existsNextData && !isLoading && !isScrollLoading}
      scrollableTarget={scrollableTarget || 'custom-table-container'}
      scrollThreshold={0.99}
      style={{ overflowY: 'hidden' }}
      className="custom-table__infinite-scroll"
    >
      <div className="custom-table__wrapper" ref={tableWrapperRef}>
        {stickyHeaderParams ? (
          <>
            {stickyHeaderBackdropHeight ? (
              <div className="custom-table__thead-sticky-backdrop" ref={stickyHeaderBackdropRef} />
            ) : null}
  
            <div className="custom-table__thead-sticky" ref={stickyHeaderRef}>
              <div>
                <table>
                  <DesktopTHead headerOptions={headerOptions} />
                </table>
              </div>
            </div>
          </>
        ) : null}
        
        <table className="custom-table__table">
          <DesktopTHead headerOptions={headerOptions} headerRef={headerRef} />
          
          <TableContentContainer rows={rows} isLoading={isLoading}>
            <DesktopTableContent rows={rows} />
            {isScrollLoading && (
              <InfoRow>
                <i className="fa fa-spinner fa-spin fa-2x" />
              </InfoRow>
            )}
          </TableContentContainer>
        </table>
      </div>
    </InfiniteScroll>
  );
};
